import Phaser from 'phaser';
import { 
  GAME_CONSTANTS, 
  DEPTH_ZONES, 
  UI_COLORS, 
  AUDIO_SETTINGS, 
  BUBBLE_CONFIG
} from '../config';

export default class GameScene extends Phaser.Scene {
  // Game objects
  private diver!: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody;
  private bubbles!: Phaser.Physics.Arcade.Group;
  
  // Background elements
  private backgroundLayers: Phaser.GameObjects.Container[] = [];
  private parallaxElements: Phaser.GameObjects.Image[] = [];
  
  // UI elements
  private hudContainer!: HTMLDivElement; // HTML container for HUD elements
  private depthDisplay!: HTMLDivElement; // HTML element for depth display
  private oxygenDisplay!: HTMLDivElement; // HTML element for oxygen display
  private oxygenFill!: HTMLDivElement; // Cached reference to oxygen fill element
  
  // Sound effects
  private backgroundMusic!: Phaser.Sound.BaseSound;
  private bubbleSounds: Phaser.Sound.BaseSound[] = [];
  private successVoices: Phaser.Sound.BaseSound[] = [];
  private deeperVoice!: Phaser.Sound.BaseSound;
  private milestoneVoices: { [depth: number]: Phaser.Sound.BaseSound } = {};
  private reachedMilestones: Set<number> = new Set();
  
  // Game state
  private depth: number = 0;
  private oxygen: number = GAME_CONSTANTS.MAX_OXYGEN;
  private maxOxygen: number = GAME_CONSTANTS.MAX_OXYGEN;
  private backgroundHeight: number = 0;
  
  // Movement tracking
  private targetPoint: Phaser.Math.Vector2 | null = null;
  private movementLine!: Phaser.GameObjects.Graphics;
  
  // World bounds
  private worldHeight: number = GAME_CONSTANTS.WORLD_HEIGHT;
  
  // Debug
  private collisionCount: number = 0;
  
  // Depth zones
  private currentZone: number = 0;
  private depthZones = DEPTH_ZONES;
  
  // Debug elements
  private debugGraphics!: Phaser.GameObjects.Graphics;
  private showDebug: boolean = false; // Set to true to show collision areas
  
  // Diver animation properties
  private isAnimating: boolean = false;
  private diverAnimationTimer: Phaser.Time.TimerEvent | null = null;
  private lastAnimationFrame: number = 0;
  private animationSpeed: number = GAME_CONSTANTS.BASE_ANIMATION_SPEED;
  private diverFacingLeft: boolean = false; // Track if diver is facing left
  
  // Timer tracking for cleanup
  private activeTimers: Phaser.Time.TimerEvent[] = [];
  
  // UI update throttling
  private lastDepthUpdate: number = -1;
  private lastOxygenUpdate: number = -1;
  private uiUpdateCounter: number = 0;
  
  constructor() {
    super({ key: 'GameScene' });
  }

  preload(): void {
    console.log('GameScene preload started');
    
    // Preload SVG assets
    this.load.svg('bubble', '/assets/b.svg');
    
    // Regular diver sprites (facing right)
    this.load.svg('diver', '/assets/d.svg');
    this.load.svg('diver2', '/assets/d2.svg'); 
    this.load.svg('diver3', '/assets/d3.svg'); 
    this.load.svg('diver4', '/assets/d4.svg');
    this.load.svg('diver5', '/assets/d5.svg');
    
    // Flipped diver sprites (facing left)
    this.load.svg('diver-left', '/assets/df.svg');
    this.load.svg('diver2-left', '/assets/df2.svg'); 
    this.load.svg('diver3-left', '/assets/df3.svg'); 
    this.load.svg('diver4-left', '/assets/df4.svg');
    this.load.svg('diver5-left', '/assets/df5.svg');
    
    // Preload sound assets
    this.load.audio('sea-waves', '/assets/sea-waves-with-birds-loop.wav');
    this.load.audio('liquid-bubble', '/assets/liquid-bubble.wav');
    this.load.audio('water-bubble', '/assets/water-bubble.wav');
    
    // Preload success voice samples
    this.load.audio('success-yeha', '/assets/yeha.mp3');
    this.load.audio('success-got-it', '/assets/got-it.mp3');
    this.load.audio('success-ok', '/assets/ok.mp3');
    this.load.audio('success-now-talking', '/assets/now-were-talking.mp3');
    this.load.audio('success-yay', '/assets/yay.mp3');
    this.load.audio('success-oh-boy', '/assets/oh-boy.mp3');
    this.load.audio('success-oh-mamma', '/assets/oh-mamma.mp3');
    this.load.audio('success-ahh', '/assets/ahh.mp3');
    this.load.audio('success-super-speed', '/assets/super-speed.mp3');
    
    // Preload deeper voice
    this.load.audio('deeper', '/assets/deeper.mp3');
    
    // Preload milestone voice samples
    this.load.audio('milestone-100', '/assets/hundred-meters.mp3');
    this.load.audio('milestone-200', '/assets/two-hundred-meters.mp3');
    this.load.audio('milestone-300', '/assets/three-hundred-meters.mp3');
    this.load.audio('milestone-400', '/assets/four-hundred-meters.mp3');
    this.load.audio('milestone-500', '/assets/five-hundred-meters.mp3');
    
    // Add loading text
    const loadingText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      'Loading...',
      {
        fontSize: '24px',
        fontFamily: 'Arial',
        color: '#ffffff'
      }
    );
    loadingText.setOrigin(0.5);
    
    // Update loading text with progress
    this.load.on('progress', (value: number) => {
      console.log(`Loading progress: ${Math.floor(value * 100)}%`);
      loadingText.setText(`Loading: ${Math.floor(value * 100)}%`);
    });
    
    // Log errors during loading
    this.load.on('loaderror', (file: any) => {
      console.error(`Error loading file: ${file.key} (${file.url})`);
    });
    
    // Remove loading text when complete
    this.load.on('complete', () => {
      console.log('Asset loading complete');
      loadingText.destroy();
    });
  }

  create(): void {
    // Reset game state values
    this.depth = 0;
    this.oxygen = GAME_CONSTANTS.MAX_OXYGEN;
    this.backgroundHeight = 0;
    this.targetPoint = null;
    this.collisionCount = 0;
    this.backgroundLayers = [];
    this.parallaxElements = [];
    
    // Set up event listeners for scene transitions
    this.events.on('shutdown', this.cleanupDomElements, this);
    this.events.on('destroy', this.cleanupDomElements, this);
    
    // Verify assets are loaded before proceeding
    if (!this.textures.exists('diver') || !this.textures.exists('bubble')) {
      console.error('SVG assets not loaded properly');
      const errorText = this.add.text(
        this.cameras.main.width / 2,
        this.cameras.main.height / 2,
        'Error loading assets. Please refresh.',
        {
          fontSize: '18px',
          fontFamily: 'Arial',
          color: '#ffffff'
        }
      );
      errorText.setOrigin(0.5);
      return;
    }
    
    // Create and play background music
    this.setupSounds();
    
    // Create debug graphics
    this.debugGraphics = this.add.graphics();
    this.debugGraphics.setDepth(1000); // Above everything else
    
    // Create a simple underwater background
    this.createBackground();
    
    // Create parallax background elements
    this.createParallaxElements();
    
    // Set world bounds to allow diving deeper
    this.physics.world.setBounds(0, 0, this.cameras.main.width, this.worldHeight);
    
    // Create the diver character
    this.createDiver();
    
    // Create bubbles
    this.createBubbles();
    
    // Create DOM-based UI that won't be affected by camera effects
    this.createDomUI();
    
    // Set up input handling
    this.setupInput();
    
    // Start the game loop
    this.trackTimer(this.time.addEvent({
      delay: 100,
      callback: this.updateGameState,
      callbackScope: this,
      loop: true
    }));
    
    console.log('Game scene created. Press any key to start.');
  }

  update(): void {
    if (!this.diver || !this.diver.active) return;
    
    // Cache diver position and velocity for performance
    const diverY = this.diver.y;
    const diverVelX = this.diver.body.velocity.x;
    const diverVelY = this.diver.body.velocity.y;
    
    // Store previous depth for milestone checking
    const previousDepth = this.depth;
    
    // Update depth based on diver position - use original calculation
    this.depth = Math.max(0, Math.floor(diverY / GAME_CONSTANTS.DEPTH_DIVISOR));
    
    // Check for depth milestones (only if depth changed)
    if (this.depth !== previousDepth) {
      this.checkDepthMilestones(previousDepth, this.depth);
    }
    
    // Update DOM UI elements (throttled internally)
    this.updateDomUI();
    
    // Update depth zone indicator (only checks if zone changed)
    this.updateDepthZoneIndicator();
    
    // Update parallax elements (optimized to skip off-screen elements)
    this.updateParallaxElements();
    
    // Apply constant upward force (buoyancy)
    const oxygenFactor = 1 - (this.oxygen / this.maxOxygen);
    const upwardForce = GAME_CONSTANTS.BASE_UPWARD_FORCE + (oxygenFactor * GAME_CONSTANTS.UPWARD_FORCE_MULTIPLIER);
    
    // Apply the upward force only if not at the top of the screen
    if (diverY > GAME_CONSTANTS.BUOYANCY_TOP_THRESHOLD) {
      this.diver.setVelocityY(diverVelY - upwardForce);
    } else if (diverY < GAME_CONSTANTS.BUOYANCY_BOTTOM_THRESHOLD) {
      // If too close to the top, add a small downward force to prevent sticking
      this.diver.setVelocityY(Math.max(GAME_CONSTANTS.BUOYANCY_DOWNWARD_FORCE, diverVelY + GAME_CONSTANTS.BUOYANCY_DOWNWARD_FORCE));
    }
    
    // Cache velocity magnitude calculation (used multiple times)
    const velXSquared = diverVelX * diverVelX;
    const velYSquared = diverVelY * diverVelY;
    const velocityMagnitude = Math.sqrt(velXSquared + velYSquared);
    
    // Apply water resistance (drag) based on velocity
    if (velocityMagnitude > GAME_CONSTANTS.DRAG_THRESHOLD) {
      this.diver.setVelocity(diverVelX * GAME_CONSTANTS.DRAG_FACTOR, diverVelY * GAME_CONSTANTS.DRAG_FACTOR);
    }
    
    // Gradually rotate diver to align with movement direction
    if (velocityMagnitude > GAME_CONSTANTS.ROTATION_THRESHOLD) {
      const targetAngle = Math.atan2(diverVelY, diverVelX) + Math.PI/2;
      let currentAngle = this.diver.rotation;
      
      // Normalize angles (optimized)
      const angleDiff = targetAngle - currentAngle;
      if (angleDiff > Math.PI) {
        currentAngle += Math.PI * 2;
      } else if (angleDiff < -Math.PI) {
        currentAngle -= Math.PI * 2;
      }
      
      // Smoothly rotate towards the target angle
      this.diver.setRotation(Phaser.Math.Linear(currentAngle, targetAngle, GAME_CONSTANTS.ROTATION_SPEED));
    }
    
    // Limit maximum velocity to prevent extreme speeds (only clamp if needed)
    if (Math.abs(diverVelY) > GAME_CONSTANTS.MAX_VELOCITY) {
      this.diver.body.velocity.y = Phaser.Math.Clamp(diverVelY, -GAME_CONSTANTS.MAX_VELOCITY, GAME_CONSTANTS.MAX_VELOCITY);
    }
    if (Math.abs(diverVelX) > GAME_CONSTANTS.MAX_VELOCITY) {
      this.diver.body.velocity.x = Phaser.Math.Clamp(diverVelX, -GAME_CONSTANTS.MAX_VELOCITY, GAME_CONSTANTS.MAX_VELOCITY);
    }
    
    // Update the movement line if it exists
    if (this.targetPoint) {
      this.drawMovementLine();
    }
    
    // Check for bubble collisions (handled by timer, but check here if needed)
    // Note: Collision checking is done via timer event, but we can add a quick check here
    
    // Extend the background as the diver goes deeper
    this.extendBackgroundIfNeeded();
  }

  private createBackground(): void {
    // Simple blue background with gradient based on first zone
    const gradient = this.add.graphics();
    gradient.setName('background');
    gradient.fillStyle(this.depthZones[0].color, 1);
    gradient.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    
    // Set initial background height
    this.backgroundHeight = this.cameras.main.height;
    
    // Create background layers container for each depth zone
    for (let i = 0; i < this.depthZones.length; i++) {
      const layer = this.add.container(0, 0);
      layer.setDepth(-10); // Behind everything
      this.backgroundLayers.push(layer);
    }
  }

  private createParallaxElements(): void {
    // Create textures for background elements if they don't exist
    this.createBackgroundTextures();
    
    // Add more initial elements with varied parallax factors for a more layered effect
    // Distant elements (slow parallax) - make them more visible with higher alpha
    for (let i = 0; i < 5; i++) {
      this.addParallaxElement('coral', 0.2, 200 + i * 100, 0.7);
      this.addParallaxElement('seaweed', 0.2, 300 + i * 100, 0.7);
      this.addParallaxElement('rocks', 0.2, 400 + i * 100, 0.7);
    }
    
    // Mid-distance elements - add more of them
    for (let i = 0; i < 5; i++) {
      this.addParallaxElement('coral', 0.5, 250 + i * 100, 0.8);
      this.addParallaxElement('seaweed', 0.5, 350 + i * 100, 0.8);
      this.addParallaxElement('rocks', 0.5, 450 + i * 100, 0.8);
    }
    
    // Closer elements (fast parallax) - make them more visible
    for (let i = 0; i < 5; i++) {
      this.addParallaxElement('coral', 0.8, 220 + i * 100, 0.9);
      this.addParallaxElement('seaweed', 0.8, 320 + i * 100, 0.9);
      this.addParallaxElement('rocks', 0.8, 420 + i * 100, 0.9);
    }
    
    // Add more elements as the player dives deeper
    this.trackTimer(this.time.addEvent({
      delay: 1500, // More frequent generation
      callback: () => {
        // Add elements at different depths
        if (this.depth > 20) { // Start adding elements earlier
          const elementTypes = ['coral', 'seaweed', 'rocks'];
          const elementType = elementTypes[Phaser.Math.Between(0, elementTypes.length - 1)];
          
          // Randomly choose between distant, mid, or close elements
          const layerType = Phaser.Math.Between(0, 2);
          let parallaxFactor, alpha;
          
          if (layerType === 0) { // Distant
            parallaxFactor = Phaser.Math.FloatBetween(0.1, 0.3);
            alpha = Phaser.Math.FloatBetween(0.6, 0.8); // Higher alpha for better visibility
          } else if (layerType === 1) { // Mid
            parallaxFactor = Phaser.Math.FloatBetween(0.3, 0.6);
            alpha = Phaser.Math.FloatBetween(0.7, 0.9); // Higher alpha
          } else { // Close
            parallaxFactor = Phaser.Math.FloatBetween(0.6, 0.9);
            alpha = Phaser.Math.FloatBetween(0.8, 1.0);
          }
          
          const y = this.diver.y + Phaser.Math.Between(200, 500);
          this.addParallaxElement(elementType, parallaxFactor, y, alpha);
        }
      },
      callbackScope: this,
      loop: true
    }));
  }

  private createBackgroundTextures(): void {
    // Create coral texture - minimalist SVG style with brighter colors
    if (!this.textures.exists('coral')) {
      const svgString = `
        <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
          <rect x="10" y="10" width="8" height="30" fill="#00eeff" stroke="none" />
          <rect x="25" y="5" width="8" height="35" fill="#00eeff" stroke="none" />
          <rect x="40" y="15" width="8" height="25" fill="#00eeff" stroke="none" />
        </svg>
      `;
      
      // Create a new texture from the SVG string
      this.textures.addBase64('coral', this.svgToBase64(svgString));
    }
    
    // Create seaweed texture - minimalist SVG style with brighter colors
    if (!this.textures.exists('seaweed')) {
      const svgString = `
        <svg width="70" height="70" viewBox="0 0 70 70" xmlns="http://www.w3.org/2000/svg">
          <rect x="10" y="0" width="4" height="60" fill="#00ffaa" stroke="none" />
          <rect x="25" y="10" width="4" height="50" fill="#00ffaa" stroke="none" />
          <rect x="40" y="5" width="4" height="55" fill="#00ffaa" stroke="none" />
          
          <rect x="5" y="10" width="15" height="4" fill="#00ffaa" stroke="none" />
          <rect x="5" y="30" width="15" height="4" fill="#00ffaa" stroke="none" />
          <rect x="5" y="50" width="15" height="4" fill="#00ffaa" stroke="none" />
          
          <rect x="20" y="20" width="15" height="4" fill="#00ffaa" stroke="none" />
          <rect x="20" y="40" width="15" height="4" fill="#00ffaa" stroke="none" />
          
          <rect x="35" y="15" width="15" height="4" fill="#00ffaa" stroke="none" />
          <rect x="35" y="35" width="15" height="4" fill="#00ffaa" stroke="none" />
          <rect x="35" y="55" width="15" height="4" fill="#00ffaa" stroke="none" />
        </svg>
      `;
      
      // Create a new texture from the SVG string
      this.textures.addBase64('seaweed', this.svgToBase64(svgString));
    }
    
    // Create rocks texture - minimalist SVG style with more contrast
    if (!this.textures.exists('rocks')) {
      const svgString = `
        <svg width="80" height="70" viewBox="0 0 80 70" xmlns="http://www.w3.org/2000/svg">
          <rect x="10" y="10" width="30" height="20" fill="#556677" stroke="none" />
          <rect x="45" y="15" width="25" height="25" fill="#556677" stroke="none" />
          <rect x="5" y="35" width="20" height="15" fill="#556677" stroke="none" />
          <rect x="30" y="40" width="35" height="20" fill="#556677" stroke="none" />
        </svg>
      `;
      
      // Create a new texture from the SVG string
      this.textures.addBase64('rocks', this.svgToBase64(svgString));
    }
  }

  private addParallaxElement(type: string, parallaxFactor: number, y: number, alpha: number = 1): void {
    // Determine which side to place the element - distribute more evenly across the screen
    const x = Phaser.Math.Between(0, this.cameras.main.width);
    
    // Create the element
    const element = this.add.image(x, y, type);
    element.setAlpha(alpha);
    element.setData('parallaxFactor', parallaxFactor);
    element.setData('initialX', x);
    element.setDepth(-5); // Behind the diver but in front of the background
    
    // Randomly flip and scale the element
    if (Phaser.Math.Between(0, 1) === 1) {
      element.setFlipX(true);
    }
    
    // Vary the scale more for visual diversity
    const scale = Phaser.Math.FloatBetween(0.7, 2.5); // Larger max scale for more variety
    element.setScale(scale);
    
    // Add to the array for tracking
    this.parallaxElements.push(element);
    
    // Add to the appropriate background layer based on depth
    let layerIndex = 0;
    for (let i = this.depthZones.length - 1; i >= 0; i--) {
      if (y / 10 >= this.depthZones[i].depth) {
        layerIndex = i;
        break;
      }
    }
    
    // Add to the layer
    this.backgroundLayers[layerIndex].add(element);
    
    // Remove elements that are too far above the camera view
    const cleanupTimer = this.trackTimer(this.time.addEvent({
      delay: 10000,
      callback: () => {
        if (element.active && element.y < this.cameras.main.scrollY - 300) {
          element.destroy();
          const index = this.parallaxElements.indexOf(element);
          if (index > -1) {
            this.parallaxElements.splice(index, 1);
          }
          // Remove timer from tracking when cleanup completes
          const timerIndex = this.activeTimers.indexOf(cleanupTimer);
          if (timerIndex > -1) {
            this.activeTimers.splice(timerIndex, 1);
          }
        }
      },
      callbackScope: this,
      loop: false
    }));
  }

  private updateParallaxElements(): void {
    // Early exit if no elements
    if (this.parallaxElements.length === 0) return;
    
    // Cache camera values to avoid repeated calculations
    const cameraCenterX = this.cameras.main.scrollX + this.cameras.main.width / 2;
    const cameraOffsetX = cameraCenterX - this.cameras.main.width / 2;
    const cameraTop = this.cameras.main.scrollY;
    const cameraBottom = cameraTop + this.cameras.main.height;
    const diverVelocityY = this.diver.body.velocity.y;
    const hasVerticalMovement = Math.abs(diverVelocityY) > 30;
    
    // Update each parallax element's position based on camera movement
    // Only update elements that are potentially visible (within camera bounds + margin)
    const margin = 200; // Update elements slightly outside viewport for smooth transitions
    const cleanupMargin = 500; // Clean up elements this far above camera
    
    // Clean up and update in one pass
    for (let i = this.parallaxElements.length - 1; i >= 0; i--) {
      const element = this.parallaxElements[i];
      
      if (!element.active) {
        // Remove inactive elements
        this.parallaxElements.splice(i, 1);
        continue;
      }
      
      // Clean up elements that are far above the camera
      if (element.y < cameraTop - cleanupMargin) {
        element.destroy();
        this.parallaxElements.splice(i, 1);
        continue;
      }
      
      // Skip elements that are far outside the viewport for updates
      if (element.y < cameraTop - margin || element.y > cameraBottom + margin) {
        continue;
      }
      
      const parallaxFactor = element.getData('parallaxFactor');
      const initialX = element.getData('initialX');
      
      // Calculate the parallax offset - make it more pronounced
      const parallaxOffsetX = cameraOffsetX * parallaxFactor * 2.5;
      
      // Apply the parallax effect
      element.x = initialX - parallaxOffsetX;
      
      // Add a subtle vertical parallax effect based on diver movement
      if (hasVerticalMovement) {
        const verticalOffset = (diverVelocityY / 200) * parallaxFactor * 3;
        element.y += verticalOffset;
      }
    }
  }

  private extendBackgroundIfNeeded(): void {
    // Extend the background as the diver goes deeper
    const gradient = this.children.getByName('background') as Phaser.GameObjects.Graphics;
    if (!gradient) return;
    
    const cameraBottom = this.cameras.main.scrollY + this.cameras.main.height;
    const extensionThreshold = 200;
    const extensionAmount = 400;
    
    // If we're approaching the bottom of the drawn area, extend it
    if (cameraBottom > this.backgroundHeight - extensionThreshold) {
      const startY = this.backgroundHeight;
      const endY = cameraBottom + extensionAmount;
      
      // Determine the color based on current depth zone
      const zoneColor = this.getDepthZoneColor(this.depth);
      gradient.fillStyle(zoneColor, 1);
      gradient.fillRect(0, startY, this.cameras.main.width, endY - startY);
      
      // Update background height
      this.backgroundHeight = endY;
    }
  }

  private getDepthZoneColor(depth: number): number {
    // Find the appropriate zone color based on depth
    let zoneIndex = 0;
    
    for (let i = this.depthZones.length - 1; i >= 0; i--) {
      if (depth >= this.depthZones[i].depth) {
        zoneIndex = i;
        break;
      }
    }
    
    // If we're at the last zone, just return its color
    if (zoneIndex === this.depthZones.length - 1) {
      return this.depthZones[zoneIndex].color;
    }
    
    // Otherwise, interpolate between this zone and the next
    const currentZone = this.depthZones[zoneIndex];
    const nextZone = this.depthZones[zoneIndex + 1];
    
    // Calculate how far we are between zones (0-1)
    const zoneDelta = nextZone.depth - currentZone.depth;
    const depthDelta = depth - currentZone.depth;
    const zoneProgress = Math.min(1, depthDelta / zoneDelta);
    
    // Interpolate the color
    const currentColor = Phaser.Display.Color.ValueToColor(currentZone.color);
    const nextColor = Phaser.Display.Color.ValueToColor(nextZone.color);
    
    const interpolatedColor = Phaser.Display.Color.Interpolate.ColorWithColor(
      currentColor,
      nextColor,
      1,
      zoneProgress
    );
    
    return Phaser.Display.Color.GetColor(
      interpolatedColor.r,
      interpolatedColor.g,
      interpolatedColor.b
    );
  }

  private updateDepthZoneIndicator(): void {
    // Find the current zone
    let newZone = 0;
    let zoneName = this.depthZones[0].name;
    
    for (let i = this.depthZones.length - 1; i >= 0; i--) {
      if (this.depth >= this.depthZones[i].depth) {
        newZone = i;
        zoneName = this.depthZones[i].name;
        break;
      }
    }
    
    // If we've entered a new zone, show a minimalist notification
    if (newZone !== this.currentZone) {
      const previousZone = this.currentZone;
      this.currentZone = newZone;
      
      // Create zone notification - minimalist style
      const zoneText = this.add.text(
        this.cameras.main.width / 2,
        this.cameras.main.height / 3,
        zoneName,
        {
          fontSize: '24px',
          fontFamily: 'Arial',
          color: '#ffffff',
          fontStyle: 'bold'
        }
      );
      zoneText.setOrigin(0.5);
      zoneText.setScrollFactor(0);
      zoneText.setAlpha(0);
      zoneText.setDepth(100);
      
      // Fade in and out animation
      this.tweens.add({
        targets: zoneText,
        alpha: 1,
        duration: 1000,
        ease: 'Power2',
        yoyo: true,
        hold: 1500,
        onComplete: () => {
          zoneText.destroy();
        }
      });
      
      // Add a camera flash effect when entering a new zone
      this.cameras.main.flash(500, 0, 0, 0, false);
      
      // When entering a deeper zone, play the "deeper" voice
      if (newZone > previousZone && previousZone > 0) {
        this.deeperVoice.play();
      }
    }
  }

  private createDiver(): void {
    // Create the diver sprite with physics using the external SVG
    this.diver = this.physics.add.sprite(
      this.cameras.main.width / 2,
      100,
      'diver'
    );
    
    // Set the diver's depth to be in front
    this.diver.setDepth(10);
    
    // Reduce diver size by half
    this.diver.setScale(GAME_CONSTANTS.DIVER_SCALE);
    
    // Allow the diver to move freely
    this.diver.setCollideWorldBounds(false);
    
    // Make hitbox MUCH smaller - just a small point in the center
    // This will prevent false collisions
    this.diver.setSize(GAME_CONSTANTS.DIVER_HITBOX_SIZE, GAME_CONSTANTS.DIVER_HITBOX_SIZE);
    this.diver.setOffset(18, 18); // Position at the center of the diver
    
    this.diver.setOrigin(GAME_CONSTANTS.DIVER_ORIGIN_X, GAME_CONSTANTS.DIVER_ORIGIN_Y);
    
    // Enable physics body for better collision detection
    this.diver.body.enable = true;
    
    // Set up camera to follow the diver
    this.cameras.main.startFollow(
      this.diver, 
      true, 
      GAME_CONSTANTS.CAMERA_FOLLOW_LERP_X, 
      GAME_CONSTANTS.CAMERA_FOLLOW_LERP_Y
    );
    this.cameras.main.setDeadzone(GAME_CONSTANTS.CAMERA_DEADZONE_X, GAME_CONSTANTS.CAMERA_DEADZONE_Y);
    
    // Set camera bounds to match world bounds
    this.cameras.main.setBounds(0, 0, this.cameras.main.width, this.worldHeight);
    
    // Start the continuous animation
    this.continuousAnimation();
  }

  private createBubbles(): void {
    // Create a bubble group with physics
    this.bubbles = this.physics.add.group({
      collideWorldBounds: false,
      allowGravity: false
    });
    
    // Generate some initial bubbles
    this.generateBubbles(BUBBLE_CONFIG.INITIAL_COUNT);
    
    // Use our custom collision detection instead of the built-in overlap
    this.trackTimer(this.time.addEvent({
      delay: BUBBLE_CONFIG.COLLISION_CHECK_INTERVAL,
      callback: this.checkBubbleCollisions,
      callbackScope: this,
      loop: true
    }));
    
    // Timer to generate new bubbles more frequently
    this.trackTimer(this.time.addEvent({
      delay: BUBBLE_CONFIG.GENERATION_INTERVAL,
      callback: () => {
        // Generate more bubbles if there are fewer than minimum on screen
        if (this.bubbles.countActive() < BUBBLE_CONFIG.MIN_ON_SCREEN) {
          this.generateBubbles(BUBBLE_CONFIG.GENERATION_COUNT_HIGH);
        } else {
          this.generateBubbles(BUBBLE_CONFIG.GENERATION_COUNT_LOW);
        }
      },
      callbackScope: this,
      loop: true
    }));
  }

  private generateBubbles(count: number): void {
    const cameraBottom = this.cameras.main.scrollY + this.cameras.main.height;
    const cameraTop = this.cameras.main.scrollY;
    
    // Clean up off-screen bubbles before generating new ones
    this.cleanupOffScreenBubbles();
    
    for (let i = 0; i < count; i++) {
      const x = Phaser.Math.Between(20, this.cameras.main.width - 20);
      
      let y;
      if (i < count * 0.7) {
        // Generate bubbles below the camera view
        y = cameraBottom + Phaser.Math.Between(50, 300);
      } else {
        // Generate bubbles in the lower half of the camera view
        y = cameraTop + this.cameras.main.height / 2 + Phaser.Math.Between(0, this.cameras.main.height / 2);
      }
      
      // Skip this bubble if it's too close to the diver (use squared distance for performance)
      if (this.diver) {
        const dx = this.diver.x - x;
        const dy = this.diver.y - y;
        const distanceSquared = dx * dx + dy * dy;
        const minDistanceSquared = BUBBLE_CONFIG.MIN_DISTANCE_FROM_DIVER * BUBBLE_CONFIG.MIN_DISTANCE_FROM_DIVER;
        
        // If the bubble would be too close to the diver, skip it
        if (distanceSquared < minDistanceSquared) {
          continue;
        }
      }
      
      // Use Phaser's built-in pooling via group.create()
      const bubble = this.bubbles.create(x, y, 'bubble') as Phaser.Physics.Arcade.Sprite;
      const scale = Phaser.Math.FloatBetween(BUBBLE_CONFIG.SCALE_MIN, BUBBLE_CONFIG.SCALE_MAX);
      bubble.setScale(scale);
      
      if (bubble.body) {
        bubble.body.enable = true;
        bubble.setData('isCollecting', false);
        bubble.setData('scale', scale);
        
        // Make hitbox MUCH smaller - just a small point in the center
        // This will prevent false collisions
        bubble.setSize(GAME_CONSTANTS.DIVER_HITBOX_SIZE, GAME_CONSTANTS.DIVER_HITBOX_SIZE);
        bubble.setOffset(48, 48); // Center of the bubble
      }
      
      // Set bubble velocity (smaller bubbles rise faster)
      const baseSpeed = Phaser.Math.Between(BUBBLE_CONFIG.BASE_SPEED_MIN, BUBBLE_CONFIG.BASE_SPEED_MAX);
      const sizeAdjustedSpeed = baseSpeed * (1.2 - scale * BUBBLE_CONFIG.SPEED_ADJUSTMENT_FACTOR);
      
      bubble.setVelocityY(-sizeAdjustedSpeed);
      bubble.setVelocityX(Phaser.Math.Between(-10, 10));
      
      // Don't make bubbles collide with world bounds - let them go off screen
      bubble.setCollideWorldBounds(false);
      
      // Add some wobble to the bubble
      this.tweens.add({
        targets: bubble,
        x: bubble.x + Phaser.Math.Between(-20, 20),
        duration: Phaser.Math.Between(1000, 3000),
        yoyo: true,
        repeat: -1
      });
      
      // Destroy bubbles that go off the top of the screen
      const bubbleLifetimeTimer = this.trackTimer(this.time.addEvent({
        delay: BUBBLE_CONFIG.LIFETIME,
        callback: () => {
          if (bubble && bubble.active) {
            bubble.destroy();
          }
          // Remove timer from tracking when cleanup completes
          const timerIndex = this.activeTimers.indexOf(bubbleLifetimeTimer);
          if (timerIndex > -1) {
            this.activeTimers.splice(timerIndex, 1);
          }
        },
        callbackScope: this,
        loop: false
      }));
    }
  }
  
  private cleanupOffScreenBubbles(): void {
    if (!this.bubbles) return;
    
    const cameraTop = this.cameras.main.scrollY;
    
    // Clean up bubbles that are far above the camera
    this.bubbles.getChildren().forEach((bubbleObj) => {
      const bubble = bubbleObj as Phaser.Physics.Arcade.Sprite;
      if (bubble.active && bubble.y < cameraTop - BUBBLE_CONFIG.CLEANUP_MARGIN) {
        bubble.destroy();
      }
    });
  }

  private checkBubbleCollisions(): void {
    if (!this.diver || !this.bubbles || !this.diver.active) return;
    
    // Early exit if no active bubbles
    const activeBubbles = this.bubbles.getChildren().filter(b => (b as Phaser.Physics.Arcade.Sprite).active);
    if (activeBubbles.length === 0) return;
    
    // Clear debug graphics if debug is enabled
    if (this.showDebug) {
      this.debugGraphics.clear();
      this.debugGraphics.lineStyle(1, 0xff0000);
    }
    
    // Cache diver position (used for all bubbles)
    const diverCenterX = this.diver.x;
    const diverCenterY = this.diver.y;
    const diverRadius = GAME_CONSTANTS.DIVER_COLLISION_RADIUS;
    
    // Draw diver collision circle if debug is enabled
    if (this.showDebug) {
      this.debugGraphics.strokeCircle(diverCenterX, diverCenterY, diverRadius);
    }
    
    // Check each bubble (optimized with early exits)
    for (const bubbleObj of activeBubbles) {
      const bubble = bubbleObj as Phaser.Physics.Arcade.Sprite;
      
      // Skip if already collecting
      if (bubble.getData('isCollecting')) continue;
      
      // Get bubble center
      const bubbleCenterX = bubble.x;
      const bubbleCenterY = bubble.y;
      
      // Quick distance check using squared distance (avoids sqrt)
      const dx = diverCenterX - bubbleCenterX;
      const dy = diverCenterY - bubbleCenterY;
      const distanceSquared = dx * dx + dy * dy;
      
      // Get bubble scale for collision radius
      const bubbleScale = bubble.getData('scale') || 1;
      const bubbleRadius = BUBBLE_CONFIG.BASE_SIZE * bubbleScale * BUBBLE_CONFIG.COLLISION_RADIUS_FACTOR;
      const collisionRadiusSquared = (bubbleRadius + diverRadius) * (bubbleRadius + diverRadius);
      
      // Draw bubble collision circle if debug is enabled
      if (this.showDebug) {
        this.debugGraphics.strokeCircle(bubbleCenterX, bubbleCenterY, bubbleRadius);
      }
      
      // If squared distance is less than squared collision radius, we have a collision
      if (distanceSquared < collisionRadiusSquared) {
        this.collectBubble(this.diver, bubble);
        // Break after first collision to avoid multiple collisions in one frame
        break;
      }
    }
  }

  private createDomUI(): void {
    // Create a DOM container for the HUD
    this.hudContainer = document.createElement('div');
    this.hudContainer.style.position = 'absolute';
    this.hudContainer.style.top = '0';
    this.hudContainer.style.left = '0';
    this.hudContainer.style.width = '100%';
    this.hudContainer.style.height = '100%';
    this.hudContainer.style.pointerEvents = 'none'; // Don't block input
    this.hudContainer.style.zIndex = '1000';
    
    // Create depth display
    this.depthDisplay = document.createElement('div');
    this.depthDisplay.style.position = 'absolute';
    this.depthDisplay.style.top = '20px';
    this.depthDisplay.style.right = '20px';
    this.depthDisplay.style.color = '#ffffff';
    this.depthDisplay.style.fontFamily = 'Arial, sans-serif';
    this.depthDisplay.style.fontSize = '16px';
    this.depthDisplay.style.fontWeight = 'bold';
    this.depthDisplay.style.textShadow = '1px 1px 2px rgba(0,0,0,0.8)';
    this.depthDisplay.textContent = '0';
    
    // Create oxygen display
    this.oxygenDisplay = document.createElement('div');
    this.oxygenDisplay.style.position = 'absolute';
    this.oxygenDisplay.style.top = '20px';
    this.oxygenDisplay.style.left = '20px';
    this.oxygenDisplay.style.width = '150px';
    this.oxygenDisplay.style.height = '10px';
    this.oxygenDisplay.style.backgroundColor = 'rgba(0,0,0,0.5)';
    this.oxygenDisplay.style.borderRadius = '5px';
    this.oxygenDisplay.style.overflow = 'hidden';
    
    // Create oxygen bar fill
    this.oxygenFill = document.createElement('div');
    this.oxygenFill.style.width = '100%';
    this.oxygenFill.style.height = '100%';
    this.oxygenFill.style.backgroundColor = '#ffffff';
    this.oxygenFill.style.transition = 'width 0.2s ease-out';
    this.oxygenFill.id = 'oxygen-fill';
    this.oxygenDisplay.appendChild(this.oxygenFill);
    
    // Add elements to the container
    this.hudContainer.appendChild(this.depthDisplay);
    this.hudContainer.appendChild(this.oxygenDisplay);
    
    // Add the container to the document
    document.body.appendChild(this.hudContainer);
  }
  
  private updateDomUI(): void {
    // Throttle UI updates to improve performance
    this.uiUpdateCounter++;
    if (this.uiUpdateCounter % GAME_CONSTANTS.UI_UPDATE_INTERVAL !== 0) {
      return;
    }
    
    // Update depth display only if changed
    if (this.depth !== this.lastDepthUpdate) {
      this.depthDisplay.textContent = `${this.depth}`;
      this.lastDepthUpdate = this.depth;
    }
    
    // Update oxygen bar only if changed significantly
    const currentOxygen = Math.floor(this.oxygen);
    if (Math.abs(currentOxygen - this.lastOxygenUpdate) >= GAME_CONSTANTS.UI_UPDATE_THRESHOLD) {
      if (this.oxygenFill) {
        const percentage = (this.oxygen / this.maxOxygen) * 100;
        this.oxygenFill.style.width = `${percentage}%`;
        
        // Update color based on oxygen level
        if (percentage > 50) {
          this.oxygenFill.style.backgroundColor = UI_COLORS.oxygenHigh;
        } else if (percentage > 25) {
          this.oxygenFill.style.backgroundColor = UI_COLORS.oxygenMedium;
        } else {
          this.oxygenFill.style.backgroundColor = UI_COLORS.oxygenLow;
        }
      }
      this.lastOxygenUpdate = currentOxygen;
    }
  }

  private setupSounds(): void {
    // Create background music with loop
    this.backgroundMusic = this.sound.add('sea-waves', { 
      volume: AUDIO_SETTINGS.BACKGROUND_MUSIC_VOLUME, 
      loop: true 
    });
    this.backgroundMusic.play();
    
    // Create bubble pop sounds
    this.bubbleSounds = [
      this.sound.add('liquid-bubble', { volume: AUDIO_SETTINGS.BUBBLE_SOUND_VOLUME }),
      this.sound.add('water-bubble', { volume: AUDIO_SETTINGS.BUBBLE_SOUND_VOLUME })
    ];
    
    // Create success voice samples
    this.successVoices = [
      this.sound.add('success-yeha', { volume: AUDIO_SETTINGS.SUCCESS_VOICE_VOLUME }),
      this.sound.add('success-got-it', { volume: AUDIO_SETTINGS.SUCCESS_VOICE_VOLUME }),
      this.sound.add('success-ok', { volume: AUDIO_SETTINGS.SUCCESS_VOICE_VOLUME }),
      this.sound.add('success-now-talking', { volume: AUDIO_SETTINGS.SUCCESS_VOICE_VOLUME }),
      // Add new success voices
      this.sound.add('success-yay', { volume: AUDIO_SETTINGS.SUCCESS_VOICE_VOLUME }),
      this.sound.add('success-oh-boy', { volume: AUDIO_SETTINGS.SUCCESS_VOICE_VOLUME }),
      this.sound.add('success-oh-mamma', { volume: AUDIO_SETTINGS.SUCCESS_VOICE_VOLUME }),
      this.sound.add('success-ahh', { volume: AUDIO_SETTINGS.SUCCESS_VOICE_VOLUME }),
      this.sound.add('success-super-speed', { volume: AUDIO_SETTINGS.SUCCESS_VOICE_VOLUME })
    ];
    
    // Create deeper voice
    this.deeperVoice = this.sound.add('deeper', { volume: AUDIO_SETTINGS.DEEPER_VOICE_VOLUME });
    
    // Create milestone voices
    GAME_CONSTANTS.DEPTH_MILESTONES.forEach(milestone => {
      this.milestoneVoices[milestone] = this.sound.add(`milestone-${milestone}`, { 
        volume: AUDIO_SETTINGS.MILESTONE_VOICE_VOLUME 
      });
    });
  }

  private collectBubble(_diver: any, bubble: any): void {
    if (bubble.getData('isCollecting')) {
      return;
    }
    
    bubble.setData('isCollecting', true);
    
    this.collisionCount++;
    
    // Play a random bubble sound
    const randomSound = Phaser.Math.RND.pick(this.bubbleSounds);
    randomSound.play();
    
    // Increased chance to play a success voice (40% chance)
    if (Phaser.Math.Between(1, 5) <= 2) {
      // Get a random success voice
      const randomVoice = Phaser.Math.RND.pick(this.successVoices);
      // Play with a slight delay to not overlap with bubble sound
      this.time.delayedCall(150, () => {
        randomVoice.play();
      });
    }
    
    // Add a pop effect before removing the bubble
    this.addBubblePopEffect(bubble.x, bubble.y);
    
    // Visual feedback - make the bubble scale up briefly before destroying
    this.tweens.add({
      targets: bubble,
      scale: bubble.scale * 1.5,
      alpha: 0,
      duration: 150,
      onComplete: () => {
        bubble.destroy();
      }
    });
    
    // Increase oxygen with visual feedback
    const oldOxygen = this.oxygen;
    this.oxygen = Math.min(this.maxOxygen, this.oxygen + GAME_CONSTANTS.OXYGEN_GAIN_PER_BUBBLE);
    
    // Flash effect on oxygen bar
    const oxygenGain = this.oxygen - oldOxygen;
    if (oxygenGain > 0) {
      this.flashOxygenBar();
    }
    
    // Add downward acceleration to the diver
    const depthFactor = Math.min(1, this.depth / GAME_CONSTANTS.BUBBLE_COLLECTION_DEPTH_FACTOR);
    const depthBonus = GAME_CONSTANTS.BUBBLE_COLLECTION_DEPTH_BONUS * depthFactor;
    
    this.diver.setVelocityY(this.diver.body.velocity.y + GAME_CONSTANTS.BUBBLE_COLLECTION_ACCELERATION + depthBonus);
    
    // Enhanced camera zoom effect when collecting bubbles
    this.cameras.main.zoomTo(GAME_CONSTANTS.CAMERA_ZOOM_SCALE, GAME_CONSTANTS.CAMERA_ZOOM_DURATION);
    this.time.delayedCall(GAME_CONSTANTS.CAMERA_ZOOM_DURATION, () => {
      this.cameras.main.zoomTo(1.0, 350); // Zoom back out more slowly
    });
    
    // Visual feedback
    this.cameras.main.flash(100, 0, 200, 255, false);
    this.cameras.main.shake(150, 0.008); // Stronger shake
  }

  private addBubblePopEffect(x: number, y: number): void {
    // Create small particles for the pop effect using the bubble SVG
    const particles = this.add.particles(x, y, 'bubble', {
      speed: { min: 50, max: 100 },
      scale: { start: 0.3, end: 0 }, // Increased starting scale for pop effect
      lifespan: 200, // Shorter lifespan for crisper effect
      quantity: 6, // Fewer particles for cleaner look
      gravityY: -50,
      emitting: false // Only emit once
    });
    
    // Emit once and then destroy
    particles.explode();
    
    // Auto-destroy the emitter after it's done
    this.time.delayedCall(200, () => {
      particles.destroy();
    });
  }

  private flashOxygenBar(): void {
    // Flash effect for DOM-based oxygen bar
    if (this.oxygenFill) {
      const originalColor = this.oxygenFill.style.backgroundColor;
      this.oxygenFill.style.backgroundColor = '#ffffff';
      
      // Reset after a short delay
      setTimeout(() => {
        if (this.oxygenFill) {
          this.oxygenFill.style.backgroundColor = originalColor;
        }
      }, 200);
    }
  }

  private setupInput(): void {
    // Create a graphics object for the movement line
    this.movementLine = this.add.graphics();
    this.movementLine.setDepth(50);
    
    // Clear any existing event listeners to prevent duplicates
    this.input.off('pointerdown');
    
    // Handle touch/mouse input with improved mobile handling
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Log touch events for debugging
      console.log(`Touch registered at: ${pointer.worldX}, ${pointer.worldY}`);
      
      // Toggle debug mode with right click
      if (pointer.rightButtonDown()) {
        this.showDebug = !this.showDebug;
        console.log(`Debug mode: ${this.showDebug ? 'ON' : 'OFF'}`);
        if (!this.showDebug) {
          this.debugGraphics.clear();
        }
        return;
      }
      
      const targetX = pointer.worldX;
      const targetY = pointer.worldY;
      
      // Add a small threshold to prevent accidental taps
      if (this.targetPoint && 
          Phaser.Math.Distance.Between(targetX, targetY, this.targetPoint.x, this.targetPoint.y) < GAME_CONSTANTS.MOVEMENT_DISTANCE_THRESHOLD) {
        return;
      }
      
      // Create or update the target point
      if (!this.targetPoint) {
        this.targetPoint = new Phaser.Math.Vector2(targetX, targetY);
      } else {
        this.targetPoint.set(targetX, targetY);
      }
      
      // Play diver animation when clicking
      this.playDiverAnimation();
      
      // Calculate distance from diver to target point
      const distance = Phaser.Math.Distance.Between(
        this.diver.x, this.diver.y,
        targetX, targetY
      );
      
      // Set a maximum distance for calculations
      const maxDistance = this.cameras.main.width;
      const normalizedDistance = Math.min(distance, maxDistance) / maxDistance;
      
      // Consume oxygen based on distance
      const oxygenCost = GAME_CONSTANTS.OXYGEN_COST_MIN + (normalizedDistance * (GAME_CONSTANTS.OXYGEN_COST_MAX - GAME_CONSTANTS.OXYGEN_COST_MIN));
      this.oxygen = Math.max(0, this.oxygen - oxygenCost);
      
      // Calculate horizontal and vertical components of movement
      const angle = Phaser.Math.Angle.Between(this.diver.x, this.diver.y, targetX, targetY);
      
      // Determine facing direction based on click position
      this.diverFacingLeft = targetX < this.diver.x;
      
      // Base speed and distance-based bonus
      const distanceBonus = normalizedDistance * GAME_CONSTANTS.MOVEMENT_SPEED_BONUS;
      const totalSpeed = GAME_CONSTANTS.BASE_MOVEMENT_SPEED + distanceBonus;
      
      // Set velocity based on angle and speed
      const velocityX = Math.cos(angle) * totalSpeed;
      const velocityY = Math.sin(angle) * totalSpeed;
      this.diver.setVelocity(velocityX, velocityY);
      
      // Rotate diver to face movement direction
      this.diver.setRotation(angle + Math.PI/2);
      
      // Draw movement line
      this.drawMovementLine();
      
      // Add a timer to clear the line after a short delay
      this.time.delayedCall(1000, () => {
        if (this.movementLine) {
          this.movementLine.clear();
        }
        this.targetPoint = null;
      });
    }, this);
    
    // Add a touch move listener to prevent scrolling/zooming on mobile
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) {
        // Prevent default behavior when dragging
        pointer.event.preventDefault();
      }
    });
  }

  // New method to play diver animation
  private playDiverAnimation(): void {
    // If already animating in burst mode, just boost the speed
    if (this.isAnimating) {
      this.animationSpeed = 1.0; // Boost animation speed for burst movement
      return;
    }
    
    // Set to burst animation mode
    this.isAnimating = true;
    this.animationSpeed = GAME_CONSTANTS.BURST_ANIMATION_SPEED;
    
    // Start the continuous animation if it's not already running
    if (!this.diverAnimationTimer) {
      this.continuousAnimation();
    }
  }
  
  // Method to handle continuous animation at varying speeds
  private continuousAnimation(): void {
    // Cancel any existing animation timer
    if (this.diverAnimationTimer) {
      this.diverAnimationTimer.remove();
      this.diverAnimationTimer = null;
    }
    
    // Define animation frames for both directions
    const rightFacingFrames = [
      { texture: 'diver', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION },
      { texture: 'diver2', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION },
      { texture: 'diver3', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION },
      { texture: 'diver4', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION },
      { texture: 'diver5', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION }
    ];
    
    const leftFacingFrames = [
      { texture: 'diver-left', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION },
      { texture: 'diver2-left', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION },
      { texture: 'diver3-left', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION },
      { texture: 'diver4-left', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION },
      { texture: 'diver5-left', duration: GAME_CONSTANTS.ANIMATION_FRAME_DURATION }
    ];
    
    // Choose the appropriate frame set based on direction
    const animationFrames = this.diverFacingLeft ? leftFacingFrames : rightFacingFrames;
    
    // Move to the next frame
    this.lastAnimationFrame = (this.lastAnimationFrame + 1) % animationFrames.length;
    const frame = animationFrames[this.lastAnimationFrame];
    
    // Set the diver texture
    this.diver.setTexture(frame.texture);
    
    // Get the diver's current velocity
    const velocityMagnitude = Math.sqrt(
      Math.pow(this.diver.body.velocity.x, 2) + 
      Math.pow(this.diver.body.velocity.y, 2)
    );
    
    // Calculate animation speed based on velocity
    // If in burst animation mode and velocity has slowed down, transition to idle
    if (this.isAnimating && velocityMagnitude < GAME_CONSTANTS.DRAG_THRESHOLD) {
      this.isAnimating = false;
      // Gradually decrease animation speed
      this.animationSpeed = Math.max(GAME_CONSTANTS.MIN_ANIMATION_SPEED, this.animationSpeed - 0.1);
    }
    
    // Adjust animation speed based on velocity, but maintain a minimum speed
    const normalizedSpeed = Math.max(
      GAME_CONSTANTS.MIN_ANIMATION_SPEED, 
      Phaser.Math.Clamp(velocityMagnitude / GAME_CONSTANTS.MAX_VELOCITY, 0.1, 1)
    );
    
    // If not in burst mode, use normalized speed; otherwise use burst speed
    const effectiveSpeed = this.isAnimating ? this.animationSpeed : normalizedSpeed;
    
    // Calculate next frame duration - faster movement = faster animation
    const baseDuration = frame.duration;
    const adjustedDuration = baseDuration / effectiveSpeed;
    
    // Schedule the next frame
    this.diverAnimationTimer = this.time.delayedCall(adjustedDuration, () => {
      this.continuousAnimation();
    });
  }

  private drawMovementLine(): void {
    if (!this.targetPoint || !this.movementLine) return;
    
    this.movementLine.clear();
    
    // Draw a line from diver to target point - minimalist style
    this.movementLine.lineStyle(2, UI_COLORS.white, 0.1);
    this.movementLine.beginPath();
    this.movementLine.moveTo(this.diver.x, this.diver.y);
    this.movementLine.lineTo(this.targetPoint.x, this.targetPoint.y);
    this.movementLine.strokePath();
    
    // Draw a small circle at the target point
    this.movementLine.fillStyle(UI_COLORS.accent, 0.5);
    this.movementLine.fillCircle(this.targetPoint.x, this.targetPoint.y, 6);
  }

  private updateGameState(): void {
    // Decrease oxygen over time, with rate increasing with depth
    const depthFactor = Math.min(1, this.depth / GAME_CONSTANTS.OXYGEN_DEPTH_THRESHOLD);
    const depthPenalty = GAME_CONSTANTS.OXYGEN_DEPTH_PENALTY_FACTOR * depthFactor;
    
    this.oxygen = Math.max(0, this.oxygen - (GAME_CONSTANTS.OXYGEN_DECREASE_RATE + depthPenalty));
    
    // Game over if oxygen reaches 0
    if (this.oxygen <= 0) {
      this.gameOver();
    }
  }

  private gameOver(): void {
    console.log('Game over triggered');
    
    try {
      // Stop all physics
      this.physics.pause();
      
      // Disable input
      this.input.off('pointerdown');
      
      // Clean up DOM UI elements
      this.cleanupDomElements();
      
      // Fade out and transition to the GameOverScene
      this.cameras.main.fade(500, 0, 0, 0);
      this.time.delayedCall(500, () => {
        try {
          // Ensure we transition to the game over scene
          this.scene.start('GameOverScene', { depth: this.depth });
        } catch (e) {
          console.error('Error starting GameOverScene:', e);
          // As a fallback, go to the start scene directly
          this.scene.start('StartScene', { score: this.depth });
        }
      });
    } catch (e) {
      console.error('Error in gameOver method:', e);
      // Immediate fallback if there's an exception
      this.scene.start('StartScene', { score: this.depth });
    }
  }
  
  // Helper method to convert SVG string to base64 for Phaser texture
  private svgToBase64(svgString: string): string {
    // Clean up the SVG string by removing extra whitespace
    const cleanSvg = svgString.trim().replace(/\s+/g, ' ');
    // Convert to base64
    return `data:image/svg+xml;base64,${btoa(cleanSvg)}`;
  }

  // Clean up DOM elements and sounds when scene transitions or is stopped
  init(): void {
    // Clean up any previous DOM elements if they exist
    this.cleanupDomElements();
    // Reset timer tracking
    this.activeTimers = [];
  }
  
  shutdown(): void {
    this.cleanup();
  }
  
  destroy(): void {
    this.cleanup();
  }
  
  private cleanup(): void {
    // Clean up all timers
    this.activeTimers.forEach(timer => {
      if (timer && !timer.hasDispatched) {
        timer.remove();
      }
    });
    this.activeTimers = [];
    
    // Clean up animation timer
    if (this.diverAnimationTimer && !this.diverAnimationTimer.hasDispatched) {
      this.diverAnimationTimer.remove();
      this.diverAnimationTimer = null;
    }
    
    // Clean up DOM elements
    this.cleanupDomElements();
    
    // Stop all sounds
    if (this.backgroundMusic) {
      this.backgroundMusic.stop();
      this.backgroundMusic.destroy();
    }
    
    // Stop all bubble sounds
    this.bubbleSounds.forEach(sound => {
      if (sound && sound.isPlaying) {
        sound.stop();
      }
    });
    
    // Stop all success voices
    this.successVoices.forEach(sound => {
      if (sound && sound.isPlaying) {
        sound.stop();
      }
    });
    
    // Stop deeper voice
    if (this.deeperVoice && this.deeperVoice.isPlaying) {
      this.deeperVoice.stop();
    }
    
    // Stop milestone voices
    Object.values(this.milestoneVoices).forEach(sound => {
      if (sound && sound.isPlaying) {
        sound.stop();
      }
    });
    
    // Remove input listeners
    this.input.off('pointerdown');
    this.input.off('pointermove');
    
    // Clear parallax elements array
    this.parallaxElements = [];
    this.backgroundLayers = [];
  }
  
  private cleanupDomElements(): void {
    // Remove DOM elements if they exist
    if (this.hudContainer) {
      if (this.hudContainer.parentNode) {
        this.hudContainer.parentNode.removeChild(this.hudContainer);
      }
      // Clear references
      this.hudContainer = null as any;
      this.depthDisplay = null as any;
      this.oxygenDisplay = null as any;
    }
  }
  
  private trackTimer(timer: Phaser.Time.TimerEvent): Phaser.Time.TimerEvent {
    this.activeTimers.push(timer);
    return timer;
  }

  private checkDepthMilestones(previousDepth: number, currentDepth: number): void {
    // Check for predefined milestone depths (100m, 200m, etc.)
    const milestoneDepths = [100, 200, 300, 400, 500];
    
    for (const milestone of milestoneDepths) {
      // Only trigger if we've just crossed the milestone (not reached before)
      if (previousDepth < milestone && currentDepth >= milestone && !this.reachedMilestones.has(milestone)) {
        this.reachedMilestones.add(milestone);
        
        // Display achievement text
        const milestoneText = this.add.text(
          this.cameras.main.width / 2,
          this.cameras.main.height / 2 - 50,
          `${milestone}m!`,
          {
            fontSize: '48px',
            fontFamily: 'Arial',
            color: '#ffffff',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 4
          }
        );
        milestoneText.setOrigin(0.5);
        milestoneText.setDepth(999);
        
        // Add milestone animation
        this.tweens.add({
          targets: milestoneText,
          y: milestoneText.y - 100,
          alpha: 0,
          scale: 1.5,
          duration: 2000,
          ease: 'Power2',
          onComplete: () => {
            milestoneText.destroy();
          }
        });
        
        // Play milestone voice
        if (this.milestoneVoices[milestone]) {
          this.milestoneVoices[milestone].play();
        }
        
        // Flash the screen for visual feedback
        this.cameras.main.flash(500, 255, 255, 255);
        
        // Add a small camera shake effect
        this.cameras.main.shake(300, 0.005);
        
        console.log(`Milestone reached: ${milestone}m`);
      }
    }
  }
}